import React, { ReactHTML } from "react";

import { Divider, FormGroup } from "@blueprintjs/core";
import { toArray } from "core/util/ArrayUtils";
import { Registry } from "react-registry";
import { DictionaryEntryFormPanel } from "./DictionaryEntryForm";
import { LabelRegistry } from "./ILabel";
import PanelLabel from "./PanelLabel";

/**
 * helper for creating Label components, generating corresponding editor forms,
 * and providing lists of available labels to a panel.
 *
 * e.g.
 *
 * // PanelName.labels.ts - define all of the labels that you want to use
 *
 * const MyPanelLabelProvider = LabelProvider.create({
 * 	Launch: { k: "course.launch", v: "Launch" },
 *  Description: { k: "panel.description", v: "Add a description here", allowMarkdown: true }
 * });
 *
 *
 * // PanelName.panel.tsx - pass provider as arg to Panel decorator to register for the panel config dialog's forms
 *
 * @Panel({id: "my-panel", configClass: PanelConfig, labels: MyPanelLabelProvider})
 * class MyPanel extends React.PureComponent { }
 *
 *
 * // PanelName.panel.tsx - use the labels as react components
 *
 * <div>
 *  <Labels.Description args={{count: 5}} />
 * 	<ButtonView>
 *   <Labels.Launch />
 * 	</ButtonView>
 * </div>
 *
 * @author chris
 */

export interface ILabelDefinition {
	k: string; // label key
	v: string; // default value
	type?: string; // default value type (plain, markdown, html. default plain text.)
	/**
	 * list of variable names for the label editor.
	 * the values in this should match the names of the keys
	 * that the Label component will receive in its "args" prop.
	 * TBD if we can type check this.
	 */
	argNames?: string[];

	allowMarkdown?: boolean;
	allowHtml?: boolean; // use sparingly
	element?: keyof ReactHTML;
}

export interface ILabelMap {
	[key: string]: ILabelDefinition;
}

interface ILabelComponentProps<T> {
	args?: any;
}

type LabelComponent<T> = React.SFC<ILabelComponentProps<T>>;

type LabelComponents<T> = {
	[K in keyof T]: LabelComponent<T[K]>;
};

export default class LabelProvider<T extends ILabelMap> {
	private static REGISTRY_ID = "panels:labels";

	public static isValidDefinition(def: ILabelDefinition) {
		return typeof def === "object" && typeof def.k === "string" && typeof def.v === "string";
	}

	// temporary until I figure out how to use a full FragmentRenderer with FragmentRenderer
	public static createOne<T>(def: ILabelDefinition, args?: T): React.ReactElement<LabelComponent<T>> {
		return <PanelLabel {...def} />;
	}

	public static create<T extends ILabelMap>(labelMap: T): LabelProvider<T> {
		return new LabelProvider<T>(labelMap);
	}

	public static registerProvider(panelId: string, provider: LabelProvider<any>): void {
		Registry.register(provider, {id: panelId, registry: LabelProvider.REGISTRY_ID});
	}

	public static getProvider(panelId: string): LabelProvider<any> {
		return Registry.get({id: panelId, registry: LabelProvider.REGISTRY_ID}) as LabelProvider<any>;
	}

	public Labels: LabelComponents<T>;
	private data: ILabelMap;

	constructor(labelMap: T) {
		this.Labels = Object.entries(labelMap).reduce<LabelComponents<T>>((acc, [key, label]: [keyof T, any]) => {

			// register the label definition for editor
			LabelRegistry.register(label);

			// create the functional component
			acc[key] = (props) => <PanelLabel {...props} {...label} />;

			return acc;
		}, {} as LabelComponents<T>);
		this.data = labelMap;
	}

	public getData(): T {
		return this.data as T;
	}

	public getKeys(): Array<keyof T> {
		return Object.entries(this.getData()).map(([_, entry]) => entry.k);
	}

	public getDefaultMap(): { [key: string]: string } {
		return this.getLabels().reduce((a, c) => {
			a[c.k] = c.v;

			return a;
		}, {});
	}

	public getLabels(): ILabelDefinition[] {
		return toArray(this.data);
	}

	public renderEditPanel(instances: string[]): JSX.Element {

		return (
			<div>
				{this.getLabels().map((label, index) => {
					return [
						index > 0 && <Divider key={label.k + '-divider'} />,
						<FormGroup key={label.k} label={label.k}>
							<DictionaryEntryFormPanel {...label} instances={instances} />
						</FormGroup>
					];
				})}
			</div>
		);
	}

}




// WEBPACK FOOTER //
// ./src/core/components/label/LabelProvider.tsx